import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiUser, FiLock, FiLogIn, FiAlertCircle } from 'react-icons/fi';
import actLogo from '../assets/actlogo.png';
import { storeSession } from '../services/session';

const LoginPage = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Add state for ripple effect
  const [ripples, setRipples] = useState([]);

  // Generate random ripples periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (ripples.length < 5) {
        setRipples((prev) => [
          ...prev,
          {
            id: Date.now(),
            x: 30 + Math.random() * 40, // Keep ripples more centered (30-70% of screen)
            y: 20 + Math.random() * 60, // More vertical variation (20-80% of screen)
            size: 10 + Math.random() * 40, // Varying sizes
            duration: 3 + Math.random() * 4, // Varying durations
          },
        ]);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [ripples]);

  // Remove ripples after animation completes
  useEffect(() => {
    const timers = ripples.map((ripple) => {
      return setTimeout(() => {
        setRipples((prev) => prev.filter((r) => r.id !== ripple.id));
      }, ripple.duration * 1000);
    });

    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [ripples]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setErrorDetails('');

    // Normalize email to lowercase to ensure consistent database lookups
    const normalizedEmail = usernameOrEmail.toLowerCase().trim();

    try {
      const response = await axios.post('/api/xapi', {
        action: 'login',
        email: normalizedEmail,
        password: password,
        isAdmin: false,
      });

      if (response.data.message === 'Login successful') {
        storeSession(response.data.user, response.data.session);
        navigate('/onboarding');
      } else {
        // Handle different error messages
        setError('Invalid credentials');
        if (response.data.details) {
          setErrorDetails(response.data.details);
        }
      }
    } catch (error) {
      console.error('Login failed:', error.response ? error.response.data.message : error.message);
      
      // More specific error messages based on error type
      if (error.response) {
        if (error.response.status === 404) {
          setError('User not found');
          setErrorDetails('The email you entered doesn\'t exist in our system.');
        } else if (error.response.status === 401) {
          setError('Incorrect password');
          setErrorDetails('The password you entered is incorrect. Please try again or reset your password.');
        } else if (error.response.status === 429) {
          setError('Too many attempts');
          setErrorDetails('Too many failed login attempts. Please try again later or reset your password.');
        } else {
          setError('Login failed');
          setErrorDetails('Please check your credentials and try again.');
        }
      } else {
        setError('Connection error');
        setErrorDetails('Could not connect to the server. Please check your internet connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!usernameOrEmail) {
      setResetMessage('');
      setError('Email required');
      setErrorDetails('Please enter your email address to reset your password.');
      return;
    }

    // Normalize email to lowercase
    const normalizedEmail = usernameOrEmail.toLowerCase().trim();

    setIsLoading(true);
    setError('');
    setErrorDetails('');

    try {
      await axios.post('/api/xapi', {
        action: 'requestPasswordReset',
        email: normalizedEmail,
      });
      setResetMessage('If your email is in the system, you will receive a password reset link.');
      setError('');
      setErrorDetails('');
    } catch (error) {
      console.error('Failed to send reset email:', error);
      setResetMessage('');
      setError('Failed to send reset email');
      setErrorDetails('There was a problem sending the reset email. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="flex items-center justify-center min-h-screen relative overflow-hidden"
      style={{ 
        background: 'linear-gradient(135deg, #0077B6 0%, #03045E 100%)',
      }}
    >
      {/* Ripple animations */}
      {ripples.map((ripple) => (
        <motion.div
          key={ripple.id}
          className="absolute rounded-full bg-white opacity-20"
          initial={{ 
            width: 0, 
            height: 0,
            left: `${ripple.x}%`,
            top: `${ripple.y}%`,
            x: '-50%',
            y: '-50%'
          }}
          animate={{ 
            width: `${ripple.size}vw`, 
            height: `${ripple.size}vw` 
          }}
          transition={{ 
            duration: ripple.duration,
            ease: "easeOut"
          }}
        />
      ))}

      {/* Background circles for depth */}
      <motion.div 
        className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full bg-brandLight opacity-10"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.15, 0.1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse"
        }}
      />
      
      <motion.div 
        className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full bg-brandLight opacity-5"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.05, 0.1, 0.05],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          repeatType: "reverse",
          delay: 2
        }}
      />

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white p-8 rounded-2xl shadow-card w-full max-w-md relative z-10 mx-4 md:mx-0"
      >
        <div className="text-center mb-8">
          <motion.div
            className="relative inline-block mb-6"
          >
            <motion.div
              className="absolute inset-0 rounded-full opacity-20 bg-brandLight"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.3, 0.2]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                repeatType: "reverse"
              }}
              style={{ 
                transformOrigin: "center center",
                filter: "blur(10px)" 
              }}
            />
            <motion.img 
              src={actLogo} 
              alt="ACT Logo" 
              className="mx-auto w-28 md:w-32 relative z-10"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>
          <motion.h2 
            className="text-2xl md:text-3xl font-bold text-brandDark mb-2 font-heading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Welcome Back
          </motion.h2>
          <motion.p
            className="text-gray-600 text-sm md:text-base"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Affordable Cleaning Today Onboarding Portal
          </motion.p>
        </div>
        
        {error && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-red-50 border-l-4 border-danger p-4 mb-6 rounded-md flex flex-col"
          >
            <div className="flex items-center">
              <FiAlertCircle className="text-danger mr-2 flex-shrink-0" />
              <span className="text-danger font-medium">{error}</span>
            </div>
            {errorDetails && (
              <span className="text-danger text-sm mt-1 ml-6">{errorDetails}</span>
            )}
          </motion.div>
        )}

        <form onSubmit={handleLogin} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Username or Email</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiUser className="text-gray-400" />
              </div>
              <input 
                type="text" 
                placeholder="Enter your username or email" 
                value={usernameOrEmail} 
                onChange={(e) => setUsernameOrEmail(e.target.value)} 
                className="form-input pl-10 w-full text-gray-800"
                style={{ 
                  caretColor: '#0077B6',  // Custom cursor color
                  textIndent: '0',        // Reset any text indenting
                  paddingRight: '2.5rem'  // Add padding on the right
                }}
                required
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiLock className="text-gray-400" />
              </div>
              <input 
                type="password" 
                placeholder="Enter your password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                className="form-input pl-10 w-full text-gray-800"
                style={{ 
                  caretColor: '#0077B6',  // Custom cursor color
                  textIndent: '0',        // Reset any text indenting
                  paddingRight: '2.5rem'  // Add padding on the right
                }}
                required
              />
            </div>
          </div>

          <motion.button 
            type="submit"
            className="btn btn-primary w-full flex items-center justify-center overflow-hidden relative"
            whileTap={{ scale: 0.98 }}
            disabled={isLoading}
          >
            {/* Pulsating effect on the button */}
            <motion.div
              className="absolute inset-0 bg-brandBlue"
              animate={{
                background: [
                  'linear-gradient(90deg, #0077B6 0%, #00B4D8 100%)',
                  'linear-gradient(90deg, #00B4D8 0%, #0077B6 100%)',
                  'linear-gradient(90deg, #0077B6 0%, #00B4D8 100%)'
                ],
                scale: [1, 1.02, 1]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            />
            <div className="relative z-10 flex items-center justify-center">
              {isLoading ? (
                <div className="spinner mr-2"></div>
              ) : (
                <FiLogIn className="mr-2" />
              )}
              {isLoading ? 'Logging in...' : 'Login'}
            </div>
          </motion.button>
        </form>

        <div className="mt-6 text-center">
          <motion.button 
            onClick={handleForgotPassword} 
            className="text-brandBlue hover:underline text-sm"
            whileHover={{ scale: 1.05 }}
            disabled={isLoading}
          >
            Forgot your password?
          </motion.button>
          
          {resetMessage && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-success mt-2"
            >
              {resetMessage}
            </motion.p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
